/* eslint-disable camelcase */
import { CheckoutContextState } from '@Components/ProductDetailPanel/BuyFlow/CheckoutContextState';
import {
  DISTANCE_FROM_EDGE,
  DRAWER_WIDTH,
  isMobile,
  isTablet,
} from '@Constants/LeftDrawer';
import { SideNavPresenter } from '@Screens/Main/sideNavPresenter';
import { ProductsPresenter } from '@Screens/Products/productSectionPresenter';
import OwnableTheme from '@Theme';
import { consoleLog } from '@Utils/consoleLog';
import { ScrollViewManager } from '@Utils/scroll-view-manager/scroll-view-manager';
import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import Constants from 'expo-constants';
import { inject, injectable } from 'inversify';
import { action, computed } from 'mobx';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { Platform } from 'react-native';

import container from '../../ioc/ioc';
import type {
  IAuthedUpdateOrderObjectArgs,
  IAuthedUpdateOrderObjectSavedAddressArgs,
  IAuthedUpdateOrderObjectSavedCardArgs,
  IAuthedUpdateOrderObjectSavedCardSavedAddressArgs,
  IGuestUpdateOrderObjectArgs,
} from './CheckoutHandlerClass';
import type {
  IValidateAuthedOrderResult,
  TValidateAuthedOrderResultAffectedFields,
} from './interfaces';
import { CheckoutValidationFailure } from './interfaces';

import externalLinkService from '~gateways/external-link';
import type { SavedAddressDTO, SavedCardDTO } from '~gateways/ownable.dtos';
import UserAPI from '~gateways/user-api';
import { AnalyticsStore } from '~stores/analytics';
import AuthStore from '~stores/authentication';
import CheckoutStore from '~stores/checkout';
import { NavigationStore } from '~stores/navigation';
import OrderHistoryStore from '~stores/order-history';
import ProductsStore from '~stores/products';
import type { AnalyticsEventData } from '~types/analytics';
import type { IError, IProductOption, IUser } from '~types/models-types';
import { Qty } from '~types/models-types';

@injectable()
export class CheckoutPresenter {
  @inject(AuthStore)
  private AuthStore!: AuthStore;

  @inject(CheckoutStore)
  private CheckoutStore!: CheckoutStore;
  @inject(NavigationStore)
  private _navigator2Store!: NavigationStore;
  @inject(ProductsStore)
  private ProductsStore!: ProductsStore;
  @inject(ProductsPresenter)
  private ProductsPresenter!: ProductsPresenter;
  @inject(OrderHistoryStore)
  private OrderHistoryStore!: OrderHistoryStore;
  @inject(AnalyticsStore) private _analyticsStore!: AnalyticsStore;
  @inject(CheckoutContextState)
  private _checkoutContextState!: CheckoutContextState;
  @inject(ScrollViewManager) scrollViewManager!: ScrollViewManager;

  private getUserOrderSummary = UserAPI.getUserOrderSummary;
  productOptionsModalVisible = new ObservableProp(false);
  deliveryAddressModalVisible = new ObservableProp(false);
  paymentOptionsModalVisible = new ObservableProp(false);
  orderDetailsVisible = new ObservableProp(false);

  @computed get modalPositions() {
    if (isMobile) {
      return `left: 0px; right: 0px;`;
    }

    return isTablet
      ? `bottom: 0px; left: 0px`
      : `bottom: 0; left: ${
          // @ts-expect-error
          DRAWER_WIDTH + DISTANCE_FROM_EDGE
        }px;
      `;
  }

  @computed get deliveryInputActionTitle() {
    if (this.CheckoutStore.currentCheckoutMode === 'guest') {
      return 'Add';
    }
    if (
      this.CheckoutStore.addressFormVisible ||
      this.CheckoutStore.selectedAddress
    ) {
      return 'Change';
    }
    return 'Add';
  }

  @computed get paymentInputActionTitle() {
    if (this.CheckoutStore.currentCheckoutMode === 'guest') {
      return 'Add';
    }
    if (
      this.CheckoutStore.addCardFormVisible ||
      this.CheckoutStore.selectedCard
    ) {
      return 'Change';
    }
    return 'Add';
  }

  @action getBorderColor = (
    inputName: TValidateAuthedOrderResultAffectedFields
  ) =>
    (
      this.CheckoutStore?.submitOrderValidationResult?.affectedFields ?? []
    ).includes(inputName)
      ? OwnableTheme.colors.errorRed
      : OwnableTheme.colors.grayInput;

  @action closeInputForms = () => {
    this.setAddressFormVisible(false);
    this.setAddCardFormVisible(false);
  };

  @action openProductOptionModal = () => {
    this.closeModals();
    this.productOptionsModalVisible.set(true);
  };

  @action selectProductOption = (option?: IProductOption) => {
    if (option) {
      this.ProductsPresenter.handleProductOptionSelect(option);
      this.closeModals();
    }
  };

  @action onChangeAddressPressed = () => {
    this.closeModals();
    if (this.CheckoutStore.currentCheckoutMode === 'authed') {
      this.deliveryAddressModalVisible.set(true);
      return;
    }
    this.onAddNewAddressPressed();
  };

  @action selectDeliveryAddress = (data: SavedAddressDTO) => {
    this.CheckoutStore?.setSelectedAddress(data);
    this.CheckoutStore?.setCurrentCheckoutAvailableFeatures(
      this.CheckoutStore?.currentAvailableCheckoutFeatures.filter(
        (entry) => !['AddNewAddress', 'CollectDeliveryAddress'].includes(entry)
      )
    );
    this.setAddressFormVisible(false);
    this.CheckoutStore?.setCheckoutDeliveryAddressField(
      'street',
      data?.street ?? ''
    );
    this.CheckoutStore?.setCheckoutDeliveryAddressField(
      'townOrCity',
      data?.townOrCity ?? ''
    );
    this.CheckoutStore?.setCheckoutDeliveryAddressField(
      'county',
      data?.county ?? ''
    );
    this.CheckoutStore?.setCheckoutDeliveryAddressField(
      'displayName',
      data?.displayName ?? ''
    );
    this.CheckoutStore?.setCheckoutDeliveryAddressField(
      'postcode',
      data?.postcode ?? ''
    );
    this.CheckoutStore?.setCheckoutDeliveryAddressField(
      'recipientName',
      data?.recipientName ?? ''
    );
    this.CheckoutStore?.setCheckoutDeliveryAddressField(
      'phone',
      data?.phone ?? ''
    );
    this.CheckoutStore?.setAuthedUserSaveNewAddress(false);
    this.CheckoutStore?.setAuthedUserSetNewAddressAsDefault(false);
    this.CheckoutStore?.setAuthedUserSelectedDeliveryAddressId(data?.id);
    this.CheckoutStore?.setAuthedUserSelectedDeliveryAddress(data);
    this.processOrderViaSingletonClass().then();
    this.closeModals();
  };

  @action onAddNewAddressPressed = () => {
    this.closeModals();
    this.CheckoutStore.setSelectedAddress(undefined);
    if (this.CheckoutStore.addressFormVisible) {
      return;
    }
    this.setAddressFormVisible(true);
    this.CheckoutStore?.setAuthedUserSelectedDeliveryAddressId(undefined);
    this.CheckoutStore?.setAuthedUserSelectedDeliveryAddress(undefined);
    this.CheckoutStore?.setCurrentCheckoutAvailableFeatures([
      ...this.CheckoutStore?.currentAvailableCheckoutFeatures,
      'CollectDeliveryAddress',
      'AddNewAddress',
    ]);
    this.CheckoutStore?.setCheckoutDeliveryAddressField('displayName', '');
    this.CheckoutStore?.setCheckoutDeliveryAddressField('county', '');
    this.CheckoutStore?.setCheckoutDeliveryAddressField('phone', '');
    this.CheckoutStore?.setCheckoutDeliveryAddressField('postcode', '');
    this.CheckoutStore?.setCheckoutDeliveryAddressField('recipientName', '');
    this.CheckoutStore?.setCheckoutDeliveryAddressField('street', '');
    this.CheckoutStore?.setCheckoutDeliveryAddressField('townOrCity', '');
    this.processOrderViaSingletonClass().then();
  };

  @action onChangePaymentPressed = () => {
    this.closeModals();
    if (this.CheckoutStore.currentCheckoutMode === 'authed') {
      this.paymentOptionsModalVisible.set(true);
      return;
    }
    this.onAddNewCardPressed();
  };

  @action selectPaymentOption = (data: SavedCardDTO) => {
    this.setSelectedCard(data);
    this.setAddCardFormVisible(false);
    this.CheckoutStore?.setAuthedUserSelectedPaymentMethod(data ?? undefined);
    this.setPaymentIntentId(undefined);
    this.processOrderViaSingletonClass().then();
    this.closeModals();
  };

  @action onAddNewCardPressed = () => {
    this.closeModals();
    if (this.CheckoutStore.addCardFormVisible) {
      return;
    }
    this.setAddCardFormVisible(true);
    this.CheckoutStore?.setUseDeliveryAddressForBilling(false);
    this.CheckoutStore?.setCardDetailsComplete(false);
    this.CheckoutStore?.setCheckoutBillingAddressField('county', '');
    this.CheckoutStore?.setCheckoutBillingAddressField('displayName', '');
    this.CheckoutStore?.setCheckoutBillingAddressField('phone', '');
    this.CheckoutStore?.setCheckoutBillingAddressField('postcode', '');
    this.CheckoutStore?.setCheckoutBillingAddressField('recipientName', '');
    this.CheckoutStore?.setCheckoutBillingAddressField('street', '');
    this.CheckoutStore?.setCheckoutBillingAddressField('townOrCity', '');
    this.setSelectedCard(undefined);
    this.CheckoutStore?.setAuthedUserSelectedPaymentMethod(undefined);
    this.setPaymentIntentId(undefined);
    this.processOrderViaSingletonClass().then();
  };

  @action closeModals = () => {
    this.paymentOptionsModalVisible.set(false);
    this.deliveryAddressModalVisible.set(false);
    this.productOptionsModalVisible.set(false);
  };

  @action handlePlaceOrderPress = async (callback: () => void) => {
    this.CheckoutStore.setOrderErrorMessage(undefined);
    this.processOrderViaSingletonClass().then();
    const result = this.validateOrderBeforeSubmit();
    this.CheckoutStore.setSubmitOrderValidationResult(result);

    if (!result.result) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    await callback();
  };

  @action processOrderViaSingletonClass = async () => {
    // Create a structure that can be used for processing the order for each possible combination
    // Whenever you make changes to the order, you can call this function to propagate the changes to the singleton
    //
    // Some of these parameters are used for processing locally, while others are passed along to the API.
    // The parameters used for processing are:
    // - `useSavedAddress`
    //    - The user has selected a saved address
    // - `useSavedCard`
    //    - The user has selected a saved card
    // - `type`
    //    - Whether the checkout is for a guest or a registered user
    //
    // All other properties are passed to the API
    const newObject = (() => {
      switch (true) {
        // Authed user with a selected saved card and selected saved address
        case this.AuthStore.currentUser?.id &&
          Boolean(this.CheckoutStore.selectedCard?.id) &&
          Boolean(this.CheckoutStore.authedUserSelectedDeliveryAddressId):
          return {
            type: 'authed',
            useSavedAddress: true,
            useSavedCard: true,
            productId: this._navigator2Store.activeProductId,
            productOptionId:
              this.ProductsStore.currentProductSelectedOption?.id ?? null,
            deliveryDetails: {
              savedAddressId:
                this.CheckoutStore.authedUserSelectedDeliveryAddressId,
            },
            paymentDetails: {
              savedCardId: this.CheckoutStore.selectedCard?.id,
            },
            quantity: this.CheckoutStore.quantity,
          } as unknown as IAuthedUpdateOrderObjectSavedCardSavedAddressArgs;

        // Authed user without a selected saved card but a selected saved address
        case this.AuthStore.currentUser?.id &&
          !this.CheckoutStore.selectedCard?.id &&
          Boolean(this.CheckoutStore.authedUserSelectedDeliveryAddressId):
          return {
            type: 'authed',
            useSavedAddress: true,
            useSavedCard: false,
            productId: this._navigator2Store.activeProductId,
            productOptionId:
              this.ProductsStore.currentProductSelectedOption?.id ?? null,
            deliveryDetails: {
              savedAddressId:
                this.CheckoutStore.authedUserSelectedDeliveryAddressId,
            },
            paymentDetails: {
              saveCard: this.CheckoutStore.shouldSaveCard,
              setCardAsDefault: this.CheckoutStore.shouldSaveCardDefault,
              saveCardAs: this.CheckoutStore.cardDisplayName,
              paymentMethodId: this.CheckoutStore.paymentIntentId,
            },
            quantity: this.CheckoutStore.quantity,
          } as unknown as IAuthedUpdateOrderObjectSavedAddressArgs;

        // Authed user with a selected saved card and without a selected saved address
        case this.AuthStore.currentUser?.id &&
          Boolean(this.CheckoutStore.selectedCard?.id) &&
          !this.CheckoutStore.authedUserSelectedDeliveryAddressId:
          return {
            type: 'authed',
            useSavedAddress: false,
            useSavedCard: true,
            productId: this._navigator2Store.activeProductId,
            productOptionId:
              this.ProductsStore.currentProductSelectedOption?.id ?? null,
            deliveryDetails: {
              county: this.CheckoutStore.checkoutDeliveryAddress.county,
              phoneNumber: this.CheckoutStore.checkoutDeliveryAddress.phone,
              postCode: this.CheckoutStore.checkoutDeliveryAddress.postcode,
              recipientName:
                this.CheckoutStore.checkoutDeliveryAddress.recipientName,
              street: this.CheckoutStore.checkoutDeliveryAddress.street,
              townOrCity: this.CheckoutStore.checkoutDeliveryAddress.townOrCity,
              saveAddress: this.CheckoutStore.authedUserSaveNewAddress,
              saveAddressAs:
                this.CheckoutStore.checkoutDeliveryAddress.displayName,
              setAddressAsDefault:
                this.CheckoutStore.authedUserSetNewAddressAsDefault,
            },
            paymentDetails: {
              savedCardId: this.CheckoutStore.selectedCard?.id,
            },
            quantity: this.CheckoutStore.quantity,
          } as unknown as IAuthedUpdateOrderObjectSavedCardArgs;

        // Authed user without a selected saved card and without a selected saved address
        case this.AuthStore.currentUser?.id &&
          !this.CheckoutStore.selectedCard?.id &&
          !this.CheckoutStore.authedUserSelectedDeliveryAddressId:
          return {
            type: 'authed',
            useSavedAddress: false,
            useSavedCard: false,
            productId: this._navigator2Store.activeProductId,
            productOptionId:
              this.ProductsStore.currentProductSelectedOption?.id ?? null,
            deliveryDetails: {
              county: this.CheckoutStore.checkoutDeliveryAddress.county,
              phoneNumber: this.CheckoutStore.checkoutDeliveryAddress.phone,
              postCode: this.CheckoutStore.checkoutDeliveryAddress.postcode,
              recipientName:
                this.CheckoutStore.checkoutDeliveryAddress.recipientName,
              street: this.CheckoutStore.checkoutDeliveryAddress.street,
              townOrCity: this.CheckoutStore.checkoutDeliveryAddress.townOrCity,
              saveAddress: this.CheckoutStore.authedUserSaveNewAddress,
              saveAddressAs:
                this.CheckoutStore.checkoutDeliveryAddress.displayName,
              setAddressAsDefault:
                this.CheckoutStore.authedUserSetNewAddressAsDefault,
            },
            paymentDetails: {
              saveCard: this.CheckoutStore.shouldSaveCard,
              setCardAsDefault: this.CheckoutStore.shouldSaveCardDefault,
              saveCardAs: this.CheckoutStore.cardDisplayName,
              paymentMethodId: this.CheckoutStore.paymentIntentId,
            },
            quantity: this.CheckoutStore.quantity,
          } as unknown as IAuthedUpdateOrderObjectArgs;

        // Guest user
        case !this.AuthStore.currentUser?.id:
        default:
          return {
            type: 'guest',
            useSavedAddress: false,
            useSavedCard: false,
            productId: this._navigator2Store.activeProductId,
            productOptionId:
              this.ProductsStore.currentProductSelectedOption?.id ?? null,
            anonymousUserEmail: this.CheckoutStore.guestCheckoutEmail,
            anonymousUserName: this.CheckoutStore.guestCheckoutName,
            deliveryDetails: {
              county: this.CheckoutStore.checkoutDeliveryAddress.county,
              phoneNumber: this.CheckoutStore.checkoutDeliveryAddress.phone,
              postCode: this.CheckoutStore.checkoutDeliveryAddress.postcode,
              recipientName:
                this.CheckoutStore.checkoutDeliveryAddress.recipientName,
              street: this.CheckoutStore.checkoutDeliveryAddress.street,
              townOrCity: this.CheckoutStore.checkoutDeliveryAddress.townOrCity,
              saveAddress: this.CheckoutStore.authedUserSaveNewAddress,
              saveAddressAs:
                this.CheckoutStore.checkoutDeliveryAddress.displayName,
              setAddressAsDefault: false, // Guest users cannot set a default address
            },
            paymentDetails: {
              saveCard: this.CheckoutStore.shouldSaveCard,
              setCardAsDefault: this.CheckoutStore.shouldSaveCardDefault,
              saveCardAs: this.CheckoutStore.cardDisplayName,
              paymentMethodId: this.CheckoutStore.paymentIntentId,
            },
            quantity: this.CheckoutStore.quantity,
          } as unknown as IGuestUpdateOrderObjectArgs;
      }
    })();

    // Update the object in the singleton
    this.CheckoutStore.CheckoutHandlerSingleton.updateOrderObject({
      newObject,
    });
  };

  // TODO: Refactor code so that SideNavPresenter is not needed here, then remove this.
  @action readonly getSideNavPresenter = () =>
    container.get<SideNavPresenter>(SideNavPresenter);

  @action
  public readonly setGuestCheckoutName = (name: string = '') => {
    this.CheckoutStore.guestCheckoutName = name;
  };

  public readonly setGuestCheckoutEmail = (email: string = '') => {
    this.CheckoutStore.guestCheckoutEmail = email;
  };

  @action public handleCancelCheckoutFlow = () => {
    this.CheckoutStore.resetCheckoutFlow();
  };

  resetCheckoutFlow = () => {
    this.CheckoutStore.resetCheckoutFlow();
  };

  @action
  public readonly handleCheckoutOption = (option: 'guest' | 'login') => {
    if (this.CheckoutStore.guestCheckoutUserHasCompletedOrder) {
      this.CheckoutStore.setCheckoutMode('guest');
      this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
        'CollectGuestName',
        'CollectGuestEmail',
        'ConfirmGuestDetailsButton',
        'ShowOrderInfo',
        'CollectDeliveryAddress',
        'CollectPaymentAddress',
        'ChangeItemQuantity',
        'CollectPaymentCardDetails',
        'ConfirmOrderButton',
      ]);
      return;
    }
    switch (option) {
      case 'login':
        this.CheckoutStore.identityFlowStep = 1;
        this.CheckoutStore.setCheckoutMode('authed');
        break;
      case 'guest':
        this.CheckoutStore.identityFlowStep = 2;
        this.CheckoutStore.setCheckoutMode('guest');
        break;
      default: // Do nothing
    }
  };

  @action public readonly setDefaultDetailsForAuthedUser = () => {
    setImmediate(async () => {
      await this.getOrderSummary(this._navigator2Store.activeProductId);
      this.CheckoutStore.setCurrentCheckoutMode('authed');
      this.setCheckoutCardToDefaultCard();
      this.setCheckoutAddressToDefaultAddress();
      this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
        'ShowOrderInfo',
        'ChangeItemQuantity',
        'ChangePaymentOptionsDropdown',
        'ChangeDeliveryAddressesDropdown',
        'ConfirmOrderButton',
      ]);
    });
  };

  @action public handleBuyPress = async (
    supplierVisitLink: string | undefined,
    methodId: number
  ) => {
    this.logProductActionBuyAnalytics();
    this._checkoutContextState.onBuyPress();
    switch (methodId) {
      case 0:
        // eslint-disable-next-line no-case-declarations
        this.proceedWithBuyFlow().then();
        break;
      case 1:
        if (supplierVisitLink) {
          this.proceedWithSupplier(supplierVisitLink).then();
        }
        break;
      case 2:
        if (supplierVisitLink) {
          this.proceedWithSupplier(supplierVisitLink).then();
        }
        break;
      default: // do nothing
    }
  };

  @action private proceedWithSupplier = async (link: string) => {
    externalLinkService.openUrl(link, 'store');
    this._analyticsStore.logProductActionVisit({
      userId: this.AuthStore.currentUser?.id,
      productId: this.ProductsStore.currentProduct?.id!,
      productName: this.ProductsStore.currentProduct?.name!,
    });
  };

  @action public readonly proceedWithBuyFlow = async () => {
    this.ProductsStore.scrollToBuy();
    this.CheckoutStore.setCurrentCheckoutMode(
      this.AuthStore?.currentUser?.id ? 'authed' : 'guest'
    );
    this.CheckoutStore.setUiShowBuyFlow(true);
    if (!this.AuthStore?.currentUser?.id) {
      this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
        'ChooseGuestOrLogin',
        'ShowOrderInfo',
      ]);
      return;
    }
    await this.getOrderSummary(this._navigator2Store.activeProductId);
    this.setDefaultDetailsForAuthedUser();
    this.CheckoutStore.setUiShowBuyFlow(true);
    this.orderDetailsVisible.set(true);
    this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
      'ConfirmOrderButton',
      'ChangeDeliveryAddressesDropdown',
      'ChangePaymentOptionsDropdown',
    ]);
  };

  @action
  public readonly handleGuestUser = (name: string, email: string) => {
    // User MUST enter a name AND email in order to proceed through guest checkout
    if (!name || !email) {
      return;
    }
    this.CheckoutStore.guestUser = { name, email };
    this.CheckoutStore.orderFlowStep = 1;
  };

  @action
  public readonly updateQuantity = async (
    qty: Qty,
    productId: number | undefined
  ) => {
    if (qty === Qty.add) {
      this.CheckoutStore.quantity = this.CheckoutStore.quantity + 1;
    }
    if (qty === Qty.minus) {
      if (this.CheckoutStore.quantity - 1 > 0) {
        this.CheckoutStore.quantity = this.CheckoutStore.quantity - 1;
      }
    }

    if (productId) {
      const result = await this.getUserOrderSummary({
        quantity: this.CheckoutStore.quantity,
        productId,
        optionId:
          this.ProductsStore.currentProductSelectedOption?.id ?? undefined,
      });

      if (result.success) {
        this.CheckoutStore.orderSummary = result.orderSummary;
      }
    }
  };

  @action handleUpdateCheckoutOnLogin = async () => {
    if (this.AuthStore.currentUser?.id) {
      this.CheckoutStore.setGuestCheckoutUserHasCompletedOrder(false);
      this.CheckoutStore.clearForms();
      if (!this.CheckoutStore.checkoutOrderId) {
        this.setDefaultDetailsForAuthedUser();
        setImmediate(() => {
          this.setDefaultDetailsForAuthedUser();
          this.setAddCardFormVisible(false);
          this.setAddressMenuVisible(false);
          this.setAddressFormVisible(false);
          this.setCardMenuVisible(false);
          this.CheckoutStore.setCurrentCheckoutMode('authed');
          this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
            'ShowOrderInfo',
            'ChangeItemQuantity',
            'ChangePaymentOptionsDropdown',
            'ChangeDeliveryAddressesDropdown',
            'ConfirmOrderButton',
          ]);
        });
      }
    } else {
      if (this.CheckoutStore.guestCheckoutUserHasCompletedOrder) {
        setImmediate(() => {
          this.setAddCardFormVisible(false);
          this.setAddressMenuVisible(false);
          this.setAddressFormVisible(false);
          this.setCardMenuVisible(false);
          this.CheckoutStore.setCurrentCheckoutMode('guest');
          this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
            'CollectGuestName',
            'CollectGuestEmail',
            'ConfirmGuestDetailsButton',
            'ShowOrderInfo',
            'CollectDeliveryAddress',
            'CollectPaymentAddress',
            'ChangeItemQuantity',
            'CollectPaymentCardDetails',
            'ConfirmOrderButton',
          ]);
        });
      } else {
        setImmediate(() => {
          this.CheckoutStore.setGuestCheckoutUserHasCompletedOrder(false);
          this.CheckoutStore.clearForms();
        });
      }
      // Only trigger if checkout isn't complete
      if (!this.CheckoutStore.checkoutOrderId) {
        setImmediate(() => {
          this.setAddCardFormVisible(false);
          this.setAddressMenuVisible(false);
          this.setAddressFormVisible(false);
          this.setCardMenuVisible(false);
          this.CheckoutStore.setCurrentCheckoutMode('guest');
          this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
            'ChooseGuestOrLogin',
          ]);
        });
      }
    }
  };

  @action
  public readonly handleBuyLoginClick = async (
    username: string,
    password: string
  ) => this.handleLoginSubmitAsync(username, password);

  @action
  public readonly handleHistoryPress = () => {
    this.OrderHistoryStore.initOrderHistory().then();
  };

  @action
  public readonly handleLoginSubmitAsync = async (
    username: string,
    password: string
  ) => {
    const loginResult = await this.AuthStore.authenticateAsync(
      username,
      password
    );
    if ((loginResult as IError).errorCode) {
      this.AuthStore.error = loginResult as IError;
      return false;
    }
    this.AuthStore.currentUser = loginResult as IUser;

    return true;
  };

  @action public readonly handleLoginWorkflow = async () => {
    this.CheckoutStore.setUiShowBuyFlow(true);
  };

  @action readonly checkUser = () => {
    if (this.AuthStore.currentUser) {
      this.CheckoutStore.orderFlowStep = 1;
    }
  };

  @action
  public readonly setGuestCheckout = (setting: boolean) => {
    this.CheckoutStore.guestCheckout = setting;
  };

  @action setLocalAddress = (address: SavedAddressDTO) =>
    (this.CheckoutStore.localAddress = address);

  @action setAddressMenuVisible = (
    newState: boolean = !this.CheckoutStore.addressMenuVisible
  ) => (this.CheckoutStore.addressMenuVisible = newState);

  @action setAddressFormVisible = (
    newState: boolean = !this.CheckoutStore.addressFormVisible
  ) => (this.CheckoutStore.addressFormVisible = newState);

  @action setCheckoutCardToDefaultCard = () => {
    if (this.CheckoutStore.defaultSavedCard) {
      this.setSelectedCard(this.CheckoutStore.defaultSavedCard);
      this.CheckoutStore.setAuthedUserSelectedPaymentMethod(
        this.CheckoutStore.defaultSavedCard
      );
      this.CheckoutStore.setCardDetailsComplete(true);
    }
  };

  @action setCheckoutAddressToDefaultAddress = () => {
    if (this.CheckoutStore.defaultAddress) {
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'street',
        this.CheckoutStore?.defaultAddress?.street ?? ''
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'townOrCity',
        this.CheckoutStore?.defaultAddress?.townOrCity ?? ''
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'county',
        this.CheckoutStore?.defaultAddress?.county ?? ''
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'displayName',
        this.CheckoutStore?.defaultAddress?.displayName ?? ''
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'postcode',
        this.CheckoutStore?.defaultAddress?.postcode ?? ''
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'recipientName',
        this.CheckoutStore?.defaultAddress?.recipientName ?? ''
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'phone',
        this.CheckoutStore?.defaultAddress?.phone ?? ''
      );
      this.CheckoutStore.setAuthedUserSelectedDeliveryAddressId(
        this.CheckoutStore.defaultAddress?.id
      );
    }
  };

  @action getOrderSummary = async (
    productId: number | undefined,
    qty: number = 1
  ) => {
    if (productId) {
      const result = await this.getUserOrderSummary({
        quantity: qty,
        productId,
        optionId:
          this.ProductsStore.currentProductSelectedOption?.id ?? undefined,
      });
      this._analyticsStore.logBeginCheckout({
        userId: this.AuthStore.currentUser?.id,
        productId,
        ...result.orderSummary,
      });

      if (result.success) {
        this.CheckoutStore.orderSummary = result.orderSummary;
        this.setAddresses(result.orderSummary?.savedAddresses);
      }

      if (result?.orderSummary?.savedCards) {
        this.setSavedCards(result.orderSummary.savedCards);
      }
    }
  };

  @action public getAddressFromGooglePlaceId = async (placeId: string) => {
    let googleApiResponse;
    if (Platform.OS === 'web') {
      googleApiResponse = await geocodeByPlaceId(placeId);
    } else {
      const url = [
        'https://maps.googleapis.com/maps/api/place/details/json?',
        'place_id=',
        placeId,
        '&key=',
        Constants.manifest?.extra?.googlePlacesApi,
      ];
      googleApiResponse = await (await fetch(url.join(''))).json();
    }

    const address =
      Platform.OS === 'web'
        ? googleApiResponse?.[0]?.address_components
        : googleApiResponse?.result?.address_components;
    if (!address) {
      return null;
    }
    const number =
      address.find((c: any) => c.types.includes('street_number'))?.long_name ??
      '';

    const road =
      address.find((c: any) => c.types.includes('route'))?.long_name ?? '';

    const town =
      address.find((c: any) => c.types.includes('postal_town'))?.long_name ??
      '';

    const county =
      address.find((c: any) => c.types.includes('administrative_area_level_2'))
        ?.long_name ?? '';

    const postal_code =
      address.find((c: any) => c.types.includes('postal_code'))?.long_name ??
      '';

    return {
      number: number ?? '',
      road: road ?? '',
      town: town ?? '',
      county: county ?? '',
      postal_code: postal_code ?? '',
    };
  };

  @action public getBillingAddress = async (placeId: string) => {
    const place = await this.getAddressFromGooglePlaceId(placeId);

    this.CheckoutStore.setCheckoutBillingAddressField(
      'street',
      [place?.number, place?.road].join(' ')
    );
    this.CheckoutStore.setCheckoutBillingAddressField(
      'townOrCity',
      place?.town ?? ''
    );
    this.CheckoutStore.setCheckoutBillingAddressField('county', place?.county);
    this.CheckoutStore.setCheckoutBillingAddressField(
      'postcode',
      place?.postal_code
    );
  };

  @action public getDeliveryAddress = async (placeId: string) => {
    try {
      const place = await this.getAddressFromGooglePlaceId(placeId);

      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'street',
        [place?.number, place?.road].join(' ')
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'townOrCity',
        place?.town ?? ''
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'county',
        place?.county
      );
      this.CheckoutStore.setCheckoutDeliveryAddressField(
        'postcode',
        place?.postal_code
      );
    } catch (err) {
      consoleLog('Error reading address: ', err);
    }
  };

  @action setSavedCards = (cards: SavedCardDTO[] = []) => {
    this.CheckoutStore.savedCards = cards;
  };

  @action setSelectedCard = (card: SavedCardDTO | undefined = undefined) => {
    this.CheckoutStore.selectedCard = card;
    this.CheckoutStore.setCardDetailsComplete(true);
  };

  @action setPaymentIntentId = (id: string | undefined = undefined) => {
    this.CheckoutStore.paymentIntentId = id;
  };

  @action setCardMenuVisible = (
    newState: boolean = !this.CheckoutStore.cardMenuVisible
  ) => (this.CheckoutStore.cardMenuVisible = newState);

  @action setAddCardFormVisible = (
    newState: boolean = !this.CheckoutStore.addCardFormVisible
  ) => (this.CheckoutStore.addCardFormVisible = newState);

  @action setSelectedAddress = (
    address: SavedAddressDTO | undefined = undefined
  ) => {
    this.CheckoutStore.selectedAddress = address;
  };

  @action setAddresses = (
    addresses: SavedAddressDTO[] | undefined = undefined
  ) => {
    this.CheckoutStore.addresses = addresses;
  };

  // This is used in conjunction with Stripe SCA - we need to resubmit the order after user authorizes the payment
  @action setCheckoutOrderId = (orderId: number | undefined = undefined) => {
    this.CheckoutStore.checkoutOrderId = orderId;
  };

  @action validateOrderBeforeSubmit = (): IValidateAuthedOrderResult => {
    const {
      selectedCard,
      selectedAddress,
      checkoutDeliveryAddress,
      checkoutBillingAddress,
      cardDetailsComplete,
      shouldSaveCard,
      cardDisplayName,
      authedUserSaveNewAddress,
    } = this.CheckoutStore;

    const {
      // 3 below currently unused, but can be added to validation if required
      displayName: deliveryDisplayName,
      phone: deliveryPhone,
      postcode: deliveryPostcode,
      recipientName: deliveryRecipient,
      street: deliveryStreet,
      townOrCity: deliveryTown,
    } = checkoutDeliveryAddress;

    const {
      // 3 below currently unused, but can be added to validation if required
      postcode: billingPostcode,
      recipientName: billingRecipient,
      street: billingStreet,
      townOrCity: billingTown,
    } = checkoutBillingAddress;

    let hasEnteredValidNewDeliveryAddress =
      deliveryPostcode.length > 0 &&
      deliveryStreet.length > 0 &&
      deliveryTown.length > 0 &&
      deliveryRecipient.length > 0 &&
      deliveryPhone.length > 0;

    if (authedUserSaveNewAddress && !deliveryDisplayName.trim()) {
      hasEnteredValidNewDeliveryAddress = false;
    }

    let hasEnteredValidNewBillingDetails =
      billingPostcode.length > 0 &&
      billingStreet.length > 0 &&
      billingTown.length > 0 &&
      billingRecipient.length > 0 &&
      cardDetailsComplete;

    if (shouldSaveCard && !cardDisplayName.trim()) {
      hasEnteredValidNewBillingDetails = false;
    }

    const affectedFields: TValidateAuthedOrderResultAffectedFields[] = [
      billingPostcode.length <= 0 ? 'PAYMENT_CARDHOLDER_POSTCODE' : undefined,
      billingStreet.length <= 0 ? 'PAYMENT_CARDHOLDER_STREET' : undefined,
      billingTown.length <= 0 ? 'PAYMENT_CARDHOLDER_TOWN' : undefined,
      billingRecipient.length <= 0 ? 'PAYMENT_CARDHOLDER_NAME' : undefined,
      deliveryPostcode.length <= 0 ? 'DELIVERY_POSTCODE' : undefined,
      deliveryStreet.length <= 0 ? 'DELIVERY_STREET' : undefined,
      deliveryTown.length <= 0 ? 'DELIVERY_TOWN' : undefined,
      deliveryRecipient.length <= 0 ? 'DELIVERY_RECIPIENT_NAME' : undefined,
      deliveryPhone.length <= 0 ? 'DELIVERY_PHONE_NUMBER' : undefined,
      authedUserSaveNewAddress && !deliveryDisplayName.trim()
        ? 'DELIVERY_DISPLAY_NAME'
        : undefined,
      shouldSaveCard && !cardDisplayName.trim()
        ? 'CARD_DISPLAY_NAME'
        : undefined,
    ];

    if (this.CheckoutStore.currentCheckoutMode === 'authed') {
      // User has not selected an existing address and has not entered new details

      if (!selectedAddress && !hasEnteredValidNewDeliveryAddress) {
        return {
          result: false,
          reason: CheckoutValidationFailure.NO_SELECTED_ADDRESS,
          affectedFields,
        };
      }

      // User has not selected an existing card and has not entered new details
      if (!selectedCard && !hasEnteredValidNewBillingDetails) {
        return {
          result: false,
          reason: CheckoutValidationFailure.NO_SELECTED_CARD,
          affectedFields,
        };
      }

      // User has selected an existing card and address
      if (
        (selectedAddress || hasEnteredValidNewDeliveryAddress) &&
        (selectedCard || hasEnteredValidNewBillingDetails) &&
        cardDetailsComplete
      ) {
        return {
          result: true,
        };
      }
    } else {
      // User has not entered a valid address
      if (!hasEnteredValidNewDeliveryAddress) {
        return {
          result: false,
          reason: CheckoutValidationFailure.NO_NEW_ADDRESS_DETAILS_ENTERED,
          affectedFields,
        };
      }

      if (!hasEnteredValidNewBillingDetails) {
        return {
          result: false,
          reason: CheckoutValidationFailure.NO_NEW_CARD_DETAILS_ENTERED,
          affectedFields,
        };
      }

      return {
        result: true,
      };
    }
    return {
      result: true,
    };
  };

  /**
   *
   * START Checkout UI flow revision - End of July 2021
   *
   * */

  @action public readonly _onUserClickBuyButton = async () => {
    this.CheckoutStore.setCurrentCheckoutMode(
      this.AuthStore?.currentUser?.id ? 'authed' : 'guest'
    );
    this.CheckoutStore.setUiShowBuyFlow(true);
    if (!this.AuthStore?.currentUser?.id) {
      this.CheckoutStore.setCurrentCheckoutAvailableFeatures([
        'ChooseGuestOrLogin',
      ]);
      return;
    }
    this.setDefaultDetailsForAuthedUser();
    this.CheckoutStore.setUiShowBuyFlow(true);
    this.CheckoutStore.openCheckoutDetailsFlow();
  };

  @action public readonly _onUserClickLoginButton = async () => {
    const loginSuccessful = await this.handleLoginSubmitAsync(
      this.CheckoutStore.checkoutLoginEmail,
      this.CheckoutStore.checkoutLoginPassword
    );
    this._analyticsStore.logLogin({
      userId: this.AuthStore.currentUser?.id!,
      context: 'incheckout',
    });

    if (!loginSuccessful) {
      this.orderDetailsVisible.set(false);
      // FIXME: Implement?
      return;
    }
    this.orderDetailsVisible.set(true);
    this.CheckoutStore.setUiShowBuyFlow(true);
    this.setDefaultDetailsForAuthedUser();
    this.CheckoutStore.openCheckoutDetailsFlow();
  };

  @computed get identityFlowStep() {
    return this.CheckoutStore.identityFlowStep;
  }

  private logProductActionBuyAnalytics = () => {
    const logData: AnalyticsEventData<'product_action_buy'> = {
      userId: this.AuthStore.currentUser?.id,
      productId: this.ProductsStore.currentProduct?.id!,
      productName: this.ProductsStore.currentProduct?.name!,
    };

    this._analyticsStore.logProductActionBuy(logData);
  };

  /**
   *
   * END Checkout UI flow revision - End of July 2021
   *
   * */
}

const useCheckoutPresenter = () =>
  useClassStore<CheckoutPresenter>(container.get(CheckoutPresenter));

export default useCheckoutPresenter;
